import { createContext, useContext, ReactNode, useMemo } from 'react'

// Define the shape of your context data
interface CountryContextType {
  country: string
}

// Create the context with a default undefined value.
// The check in the hook will ensure it's used within a provider.
const CountryContext = createContext<CountryContextType | undefined>(undefined)

// Define the props for your CountryProvider component
interface CountryProviderProps {
  children: ReactNode // To wrap around child components
  countryValue: string // The country value fetched from _app.tsx
}

// Create the Provider component
export const CountryProvider = ({
  children,
  countryValue,
}: CountryProviderProps) => {
  // Memoize the context value to prevent unnecessary re-renders of consumers
  // if the provider re-renders but the countryValue hasn't changed.
  const contextValue = useMemo(
    () => ({ country: countryValue }),
    [countryValue]
  )

  return (
    <CountryContext.Provider value={contextValue}>
      {children}
    </CountryContext.Provider>
  )
}

// Create a custom hook for easy consumption of the context
export const useCountry = (): CountryContextType => {
  const context = useContext(CountryContext)
  if (context === undefined) {
    // This check is important to ensure the hook is used correctly
    throw new Error('useCountry must be used within a CountryProvider.')
  }
  return context
}
